import { CouchDocDraft } from '@cf-shared/deep-helpers';

/**
 * The part of the food editor that `saveThroughDraftAsync` needs to see: which draft holder
 * is standing, which object the form is bound to, and how to swap that object.
 */
export interface FoodDraftSlot<T> {
  /** the draft holder standing right now, if any */
  getHolder(): CouchDocDraft<T> | undefined;
  /** the object the form is bound to right now */
  getFood(): T;
  /** put the committed canonical document on the form, keeping the holder as it is */
  showCommitted(canonical: T): void;
  /** bind the form to a fresh draft of `canonical`, replacing the holder */
  beginEditingDraft(canonical: T): void;
}

/**
 * Save a food that may be a draft of a shared database document.
 *
 * If a draft holder is standing, the draft is merged into the canonical document first and
 * the canonical is what `saveCommittedAsync` works on and writes. Afterwards — whether the save
 * succeeded or threw — the form is put back on a FRESH draft of that canonical.
 *
 * That last step is the fix for internal #727. The holder's draft is frozen at the moment of
 * the commit. When the save threw, the page stayed open with the form bound to the canonical,
 * so the user's next edits went into the canonical; the next save then merged the frozen draft
 * over them and threw them away, silently. A fresh draft makes the next merge carry the newer
 * edits instead.
 *
 * The draft is not re-opened when the save moved the page off that document: the publish path
 * drops the holder when the food's id changes, or swaps in the server's copy (#5086). The
 * holder is then gone or no longer bound to the form, and there is nothing to re-open.
 */
export async function saveThroughDraftAsync<T>(
  slot: FoodDraftSlot<T>,
  saveCommittedAsync: () => Promise<void>,
): Promise<void> {
  const holder = slot.getHolder();
  // if the form is not editing a draft, there is nothing to merge or re-open
  if (!holder) {
    await saveCommittedAsync();
    return;
  }

  slot.showCommitted(holder.commitDraft());
  try {
    await saveCommittedAsync();
  } finally {
    // if the page is still on the same document, give the form a fresh draft of it
    if (slot.getHolder() === holder && slot.getFood() === holder.canonical) {
      slot.beginEditingDraft(holder.canonical);
    }
  }
}
